import { RATE_SHAPED_KEYS, isCosmeticKey } from './cosmetic-keys';
import { isKnownSpecies } from './species';
import { isKnownVariantKey } from './variants';
import { createPlayerProfile, normalisePlayerProfile } from '../profile/schema';
import { anchorPaidTicks } from './paid-clock';
import {
  FALLBACK_SEED,
  MAX_EARNED,
  MAX_SEED,
  MAX_SKILL,
  createState,
  type CosmeticFree,
  type SimState,
} from './state';
import { CURSOR_CEILING, TICK_MS, canAdvanceFrom, tickAtMs } from './time';

/**
 * THE SAVE.
 *
 * CONTRACT.md section 6: "A save defect cannot be undone by a patch, which is
 * why this is law rather than a task."
 *
 * Three rules hold this file together.
 *
 * 1. VERSIONED, WITH A MIGRATION FOR EVERY VERSION EVER SHIPPED. MIGRATIONS
 *    below is keyed by source version and must cover every integer from 0 to
 *    SAVE_VERSION - 1 with no gaps. save.test.ts asserts that coverage, so the
 *    day somebody bumps SAVE_VERSION without writing the migration, the suite
 *    goes red rather than the player's run going missing.
 *
 *    HONEST STATEMENT OF SCOPE: exactly one save version has ever existed
 *    (v1, this one). Version 0 is not a historical format — it is the real and
 *    reachable class of input that carries no version field at all: a truncated
 *    write, a hand-edited file, a paste from somewhere else. The chain machinery
 *    is exercised by that path from day one instead of being written for the
 *    first time under pressure.
 *
 * 2. ONE NORMALISER, AND EVERY SAVED NUMBER PASSES THROUGH IT EXACTLY ONCE.
 *    intField() below is the only numeric coercion in src/sim. save.test.ts
 *    greps the tree for Number(, parseInt(, parseFloat( and unary + coercion and
 *    fails if a second one appears. normaliseEnvelope() is called from exactly
 *    one place in the load path, and it is idempotent, which is what "exactly
 *    once" is worth asserting as.
 *
 * 3. HOSTILE INPUT NEVER THROWS. Not "usually does not throw": importSave
 *    returns a result object, every parse sits in a try/catch, and every field
 *    has a fallback. save.test.ts drives twelve hostile shapes through import
 *    AND THEN through advance(), because a save that loads and then explodes on
 *    the first tick is the same outage.
 *
 * WHAT IS DELIBERATELY NOT IN SimState: the wardrobe. Cosmetics live beside the
 * simulation in the envelope, never inside it, so the rate engine in step.ts
 * cannot read what a player owns even by accident. See entitlements.ts.
 */

export const SAVE_VERSION = 2;

/** Ceilings for fields the running simulation does not itself bound. */
const MAX_CAST_PROGRESS = 4096;
const MAX_EPOCH_MS = 8_640_000_000_000_000; // the ECMAScript Date range
/**
 * The longest id anything in this lane accepts. EXPORTED because persistence.ts
 * needs the same number: SIM-D6 was a second length rule living there, so the
 * state a player was looking at was not the state that came back.
 */
export const MAX_ID_LENGTH = 64;
const MAX_CATCH_KEYS = 512;
const MAX_WARDROBE_KEYS = 512;

/**
 * Budget for fields written by a build NEWER than this one. They are carried
 * verbatim rather than deleted; see `forward` on SaveEnvelope.
 */
const MAX_FORWARD_KEYS = 32;
const MAX_FORWARD_DEPTH = 4;
const MAX_FORWARD_ARRAY = 64;
const MAX_FORWARD_JSON = 8_192;

/** The highest version number a save may claim. A bound, not a schema. */
const MAX_SAVE_VERSION = 1_000_000;

/** The envelope keys this build understands. Anything else is forward data. */
const KNOWN_ENVELOPE_KEYS = new Set(['v', 'sim', 'wardrobe', 'profile', 'forward']);

/** The SimState keys this build understands. Anything else is forward data. */
const KNOWN_SIM_KEYS = new Set([
  'seed',
  'epochMs',
  'tick',
  'xp',
  'coins',
  'catches',
  'junk',
  'mastery',
  'records',
  'specials',
  'weightG',
  'castProgress',
  'targetSpecies',
  'skill',
  'skippedTicks',
  'variants',
]);

/** Keys that must never be copied out of untrusted input, at any depth. */
const POISON_KEYS = new Set(['__proto__', 'constructor', 'prototype']);

/**
 * Keys a carried-forward field may never use, at any depth.
 *
 * Forward-carrying keeps fields this build cannot read, which is a new place a
 * hostile save could try to park `offlineHours` or `multiplier` and wait for a
 * later build to grow a reader for it. Nothing in this build could act on one —
 * the rate engine cannot even import this file — but a save format that CAN
 * hold a rate lever is one refactor away from a save format that does.
 * entitlements.ts already owns the list of names that shape a rate, so the same
 * list is reused here rather than a second one being written.
 */
const FORWARD_FORBIDDEN_KEYS: ReadonlySet<string> = new Set<string>(RATE_SHAPED_KEYS);

export interface Wardrobe {
  /** Entitlement ids the player owns. Access only; see entitlements.ts. */
  readonly owned: readonly string[];
  /** Cosmetic keys currently worn. */
  readonly equipped: readonly string[];
}

/** Anything JSON can hold. The shape a field written by a newer build arrives in. */
export type JsonValue =
  | null
  | boolean
  | number
  | string
  | readonly JsonValue[]
  | { readonly [key: string]: JsonValue };

/**
 * FIELDS THIS BUILD DOES NOT UNDERSTAND, KEPT ANYWAY.
 *
 * A save written by a newer build carries fields this one has never heard of.
 * Deleting them and writing the result back is how a rollback, a cached tab or a
 * second device destroys progress that was really earned — the exact thing
 * CONTRACT.md section 6 forbids, and the same reasoning normaliseCatches already
 * applies to unknown SPECIES IDS. They are sanitised, bounded and carried
 * through export untouched; nothing in this build ever reads them.
 */
export interface ForwardFields {
  /** Unknown keys found on `sim`. */
  readonly sim: { readonly [key: string]: JsonValue };
  /** Unknown keys found on the envelope itself. */
  readonly envelope: { readonly [key: string]: JsonValue };
}

export interface SaveEnvelope {
  /**
   * The version the save CLAIMS. A save from the future keeps its own number:
   * stamping it down to SAVE_VERSION would be exactly the "rewriting a newer
   * save into an older shape and calling it upgraded" that migrate() forbids.
   */
  readonly v: number;
  readonly sim: SimState;
  readonly wardrobe: Wardrobe;
  /** Product state, kept outside SimState so it cannot affect rates or RNG. */
  readonly profile?: unknown;
  readonly forward: ForwardFields;
}

/**
 * THE NON-VACUITY CONTROL FOR `SIM_STATE_IS_COSMETIC_FREE`.
 *
 * `CosmeticFree<SimState>` is asserted `true` in state.ts. An assertion that
 * cannot come out false is an assertion about nothing, so here is the same
 * predicate on the type that DOES carry a purchase record — the envelope. If
 * this stops being `false`, the predicate has gone blind and the assertion on
 * the other side is worthless; `tsc` says so on the same run.
 *
 * AND IT IS THE FINDING, WRITTEN AS A TYPE. `SaveEnvelope` is exactly the object
 * that puts `wardrobe` in the same scope as `sim`, in `save.ts` and
 * `persistence.ts`, which is where six of the eight round-4 plants lived. The
 * split that would make this `true` is not available: the save file holds both
 * halves, so SOME module must hold both halves, and no reachability rule can
 * reach inside a function scope. That is why the guarantee is measured on
 * values in `tools/instruments/paywin.mjs` rule C and not asserted here.
 */
export const SAVE_ENVELOPE_CARRIES_OWNERSHIP: CosmeticFree<SaveEnvelope> = false;

// ---------------------------------------------------------------------------
// THE NORMALISER. One function for numbers, one for strings. Nothing else in
// src/sim coerces a value.
// ---------------------------------------------------------------------------

/**
 * THE single numeric normaliser.
 *
 * Handles, in this order: a non-number that is not a numeric string (fallback);
 * NaN and both infinities (fallback, because they are corruption, not a large
 * value); a fraction (truncated toward zero, so a seed of 1.5 becomes 1); and a
 * value outside bounds (clamped, so a negative tick becomes 0).
 *
 * Every saved number in this game is an integer, which is why truncation is the
 * right rounding here and why the offline invariant can be asserted with deep
 * equality and no tolerance at all.
 */
export function intField(value: unknown, fallback: number, min: number, max: number): number {
  let n: number;
  if (typeof value === 'number') n = value;
  else if (typeof value === 'string' && value.trim() !== '') n = Number(value);
  else return fallback;
  if (!Number.isFinite(n)) return fallback;
  const i = Math.trunc(n);
  if (i < min) return min;
  if (i > max) return max;
  return i;
}

/**
 * The one string predicate: length-bounded, poison-key-free, printable.
 *
 * It is a predicate rather than inline checks because two callers need the same
 * rule — strField, which substitutes a fallback, and sanitiseJson, which drops
 * the field entirely. Two copies of a validation rule is one copy that can drift.
 */
function isCleanString(value: unknown, maxLength: number): value is string {
  if (typeof value !== 'string') return false;
  if (value.length > maxLength) return false;
  if (POISON_KEYS.has(value)) return false;
  // Control characters never appear in a legitimate id and are the classic
  // vector for making a rendered log or an export string lie about itself.
  for (let i = 0; i < value.length; i++) {
    const code = value.charCodeAt(i);
    if (code < 32 || code === 127) return false;
  }
  return true;
}

/** The single string normaliser: length-bounded, poison-key-free, printable. */
export function strField(value: unknown, fallback: string, maxLength: number): string {
  return isCleanString(value, maxLength) ? value : fallback;
}

/**
 * Coerce anything into a plain, safe record.
 *
 * It COPIES rather than casting, for two reasons that both bite in practice.
 * Poison keys are dropped structurally here, once, instead of being remembered
 * at every use site. And a property whose getter throws — an exotic input, but a
 * reachable one the moment a save object arrives from anywhere but JSON.parse —
 * costs that one field rather than the whole load.
 */
function asRecord(value: unknown): Record<string, unknown> {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return {};
  let keys: string[];
  try {
    keys = Object.keys(value);
  } catch {
    return {};
  }
  const out: Record<string, unknown> = {};
  for (const key of keys) {
    if (POISON_KEYS.has(key)) continue;
    try {
      out[key] = (value as Record<string, unknown>)[key];
    } catch {
      // A throwing getter is not a field. Skip it and keep the rest of the save.
    }
  }
  return out;
}

/**
 * A PER-SPECIES TALLY: the catch counts, and now the mastery totals too.
 *
 * ONE FUNCTION FOR BOTH MAPS, and the reason is the same one that made
 * `withTallyEntry` one function in mastery.ts. Every paragraph below is a rule
 * about a map of earned integers keyed by species id, and `mastery` is exactly
 * that. A second copy of these rules is a second place the eviction order, the
 * MAX_EARNED bound or the unknown-id rule can be got wrong, and the round-2
 * defect this whole file is scarred by was one rule enforced on one field while
 * six fields of the same kind went unguarded. `label` names the map in the load
 * notes and is the only thing that differs between the two calls.
 *
 * UNKNOWN SPECIES IDS ARE KEPT, not dropped. A save written by a newer build
 * carries ids this build has never heard of, and deleting them would destroy
 * earned progress on the way back — the exact thing CONTRACT.md section 6
 * forbids. They are normalised like any other count and simply never read by
 * the rate engine, which only ever looks up ids it knows.
 *
 * THE BOUND, AND WHY IT IS ORDERED THE WAY IT IS. The map is capped so a hostile
 * import cannot make the save unbounded. An earlier version kept the first 512
 * keys in ALPHABETICAL order, which is a property with nothing to do with what a
 * player earned: 512 injected keys named `a0000..a0511` evicted every real
 * species — 'a0000' sorts before 'cod' — and the collection log came back empty
 * with no note. That is a nerf to earned progress performed by the loader, and
 * CONTRACT.md section 6 forbids it.
 *
 * WHAT `isKnownKey` IS FOR, and it is the ONLY thing in this function that ever
 * had an opinion about what a key MEANS. Three of the four maps are keyed by
 * species id; `variants` is keyed `<species>:<variant>`. Rather than a fourth
 * normaliser, or a hard-coded `isKnownSpecies` that would silently rank every
 * real form key as unknown, the caller says how to recognise one. It affects
 * RETENTION ORDER under the cap and nothing else: an unrecognised key is still
 * kept, still normalised, still exported.
 *
 * Retention is therefore ordered by VALUE, not by name:
 *   1. species this build knows are kept ahead of ids it does not,
 *   2. then the larger count,
 *   3. then the id, only to make the tie deterministic.
 * A real catch can now only be evicted by another real catch, and anything
 * dropped is REPORTED rather than lost silently.
 */
function normaliseTally(
  value: unknown,
  isKnownKey: (key: string) => boolean = isKnownSpecies,
): {
  readonly tally: Record<string, number>;
  readonly dropped: number;
  readonly clamped: number;
} {
  const src = asRecord(value);
  const entries: Array<{ id: string; count: number; known: boolean }> = [];
  let clamped = 0;
  for (const key of Object.keys(src)) {
    const id = strField(key, '', MAX_ID_LENGTH);
    if (id === '') continue;
    // A COUNT IS EARNED PROGRESS, so it is bounded by MAX_EARNED and by nothing
    // narrower - see FIELD_CLASS in state.ts. It used to be bounded by
    // MAX_COUNT, which is this build's gameplay ceiling and not a bound any
    // other build's number has to obey.
    const count = intField(src[key], 0, 0, MAX_EARNED);
    if (count <= 0) continue;
    if (intField(src[key], 0, 0, Number.MAX_SAFE_INTEGER) > count) clamped += 1;
    entries.push({ id, count, known: isKnownKey(id) });
  }
  // SELECTION is by value: a real catch may only be evicted by something worth
  // more. EMISSION is by id, because the order a map serialises in must not
  // depend on how it was assembled - see sortedKeys and SIM-D2. The two were
  // one step before, which coupled "what survives the cap" to "what order the
  // collection log renders in" and made the second reorder itself silently
  // whenever two counts crossed.
  entries.sort((a, b) => {
    if (a.known !== b.known) return a.known ? -1 : 1;
    if (a.count !== b.count) return b.count - a.count;
    return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
  });
  const kept = entries.slice(0, MAX_CATCH_KEYS);
  kept.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
  const out: Record<string, number> = {};
  for (const e of kept) out[e.id] = e.count;
  return { tally: out, dropped: Math.max(0, entries.length - MAX_CATCH_KEYS), clamped };
}

/**
 * AN EARNED QUANTITY, LOADED, AND WHAT IT COST TO LOAD IT.
 *
 * SIM-D4. The per-field MAX_* in state.ts stop a value RISING in this build;
 * they are not bounds a loader may impose on a value a newer build awarded.
 * The only bound here is MAX_EARNED, which exists for arithmetic safety, and
 * any reduction at all is named in the notes rather than performed in silence.
 *
 * It asks intField twice: once with the real ceiling and once with none, purely
 * so the note can say what the first answer gave up. That is not a second
 * coercion rule - there is still exactly one function in this lane that turns an
 * unknown into a number, which instrument.test.ts asserts by grep.
 */
function earnedField(value: unknown, name: string, reduced: string[]): number {
  const kept = intField(value, 0, 0, MAX_EARNED);
  const offered = intField(value, 0, 0, Number.MAX_SAFE_INTEGER);
  if (offered > kept) reduced.push(`clamped:${name}:${offered}->${kept}`);
  return kept;
}

/**
 * Sanitise an unknown value into something JSON can hold and this file can
 * carry forward safely: bounded depth, bounded arrays, no poison keys, no
 * NaN/Infinity, no control characters. Anything that does not fit is DROPPED
 * rather than coerced, because a forward field this build cannot read is not a
 * field it should be inventing a value for.
 */
function sanitiseJson(value: unknown, depth: number): JsonValue | undefined {
  if (value === null) return null;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
  if (isCleanString(value, MAX_FORWARD_JSON)) return value;
  if (typeof value === 'string') return undefined;
  if (depth >= MAX_FORWARD_DEPTH) return undefined;
  if (Array.isArray(value)) {
    const out: JsonValue[] = [];
    for (const item of value.slice(0, MAX_FORWARD_ARRAY)) {
      const v = sanitiseJson(item, depth + 1);
      if (v !== undefined) out.push(v);
    }
    return out;
  }
  if (typeof value !== 'object') return undefined;
  const rec = asRecord(value);
  const out: Record<string, JsonValue> = {};
  let kept = 0;
  for (const key of Object.keys(rec).sort()) {
    if (kept >= MAX_FORWARD_KEYS) break;
    if (!isCleanString(key, MAX_ID_LENGTH)) continue;
    if (FORWARD_FORBIDDEN_KEYS.has(key)) continue;
    const v = sanitiseJson(rec[key], depth + 1);
    if (v === undefined) continue;
    out[key] = v;
    kept += 1;
  }
  return out;
}

/** Unknown keys of `src`, sanitised and bounded as a whole. */
function forwardOf(src: Record<string, unknown>, known: ReadonlySet<string>): {
  readonly [key: string]: JsonValue;
} {
  const out: Record<string, JsonValue> = {};
  let kept = 0;
  for (const key of Object.keys(src).sort()) {
    if (kept >= MAX_FORWARD_KEYS) break;
    if (known.has(key)) continue;
    if (!isCleanString(key, MAX_ID_LENGTH)) continue;
    if (FORWARD_FORBIDDEN_KEYS.has(key)) continue;
    const v = sanitiseJson(src[key], 1);
    if (v === undefined) continue;
    out[key] = v;
    kept += 1;
  }
  return out;
}

/**
 * One budget for the whole bag, checked AFTER sanitising and AFTER merging
 * rather than guessed before either: an unbounded carry-forward is an unbounded
 * save, and two halves that each fit do not mean their union does.
 */
function budgeted(bag: { readonly [key: string]: JsonValue }): {
  readonly [key: string]: JsonValue;
} {
  return canonicalJson(bag).length > MAX_FORWARD_JSON ? {} : sortedKeys(bag);
}

/**
 * The same object with its keys inserted in sorted order.
 *
 * SIM-D2. Two sorted bags spread over each other are NOT sorted — every key the
 * second one adds lands at the end — so a forward bag that survived one round
 * trip could serialise differently from the identical bag on the next. Sorting
 * once, here, is what keeps every object this file builds in one canonical
 * order and the whole round trip a fixed point on raw bytes.
 */
function sortedKeys(bag: { readonly [key: string]: JsonValue }): {
  readonly [key: string]: JsonValue;
} {
  const out: Record<string, JsonValue> = {};
  for (const key of Object.keys(bag).sort()) out[key] = bag[key] as JsonValue;
  return out;
}

function normaliseStringList(
  value: unknown,
  limit: number,
  accept: (s: string) => boolean,
): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  for (const raw of value) {
    if (out.length >= limit) break;
    const s = strField(raw, '', MAX_ID_LENGTH);
    if (s === '' || seen.has(s) || !accept(s)) continue;
    seen.add(s);
    out.push(s);
  }
  out.sort();
  return out;
}

function normaliseSim(value: unknown): { readonly sim: SimState; readonly notes: readonly string[] } {
  const r = asRecord(value);
  const { tally: catches, dropped, clamped } = normaliseTally(r['catches']);
  // THE SECOND EARNED MAP, THROUGH THE SAME DOOR. A separate normaliser here
  // would be the round-2 shape exactly: one rule enforced on the field a critic
  // happened to name, and the next field of the same kind left to a later round.
  const mastery = normaliseTally(r['mastery']);
  // THE THIRD EARNED MAP, THROUGH THE SAME DOOR AGAIN, and this one is a
  // MAXIMUM rather than a sum. Nothing in `normaliseTally` cares which: every
  // rule it enforces — bounded by MAX_EARNED and nothing narrower, unknown ids
  // KEPT, retention ordered by value so a real entry can only be evicted by a
  // larger one, emission ordered by id, every reduction reported — is a rule
  // about a map of earned integers keyed by species id. A separate normaliser
  // for the records map would be the round-2 shape a third time.
  const records = normaliseTally(r['records']);
  // THE FOURTH EARNED MAP, THROUGH THE SAME DOOR, AND THE FIRST WHOSE KEYS ARE
  // NOT SPECIES IDS. A form tally is keyed `<species>:<variant>`, so the one
  // thing `normaliseTally` did that assumed a species id — ranking a key this
  // build knows ahead of one it does not, for retention under the 512-key cap —
  // is now an ARGUMENT rather than a hard-coded call. Every other rule in that
  // function is a rule about a map of earned integers and needed no opinion
  // about what the key means. Passing the predicate is what keeps this the same
  // door instead of a fourth normaliser, which is the shape round 2 was scarred
  // by and which this file has now refused four times.
  const variants = normaliseTally(r['variants'], isKnownVariantKey);
  const reduced: string[] = [];
  // KEYS IN SORTED ORDER. See createState in state.ts: every constructor of a
  // state or an envelope in this lane builds in one canonical order, which is
  // what makes openSave(exportSave(e)) a fixed point on RAW bytes rather than
  // only under a comparator that sorts the difference away (SIM-D2).
  const sim: SimState = {
    castProgress: intField(r['castProgress'], 0, 0, MAX_CAST_PROGRESS),
    catches,
    coins: earnedField(r['coins'], 'coins', reduced),
    epochMs: intField(r['epochMs'], 0, -MAX_EPOCH_MS, MAX_EPOCH_MS),
    junk: earnedField(r['junk'], 'junk', reduced),
    mastery: mastery.tally,
    records: records.tally,
    seed: intField(r['seed'], FALLBACK_SEED, 0, MAX_SEED),
    skill: intField(r['skill'], 0, 0, MAX_SKILL),
    skippedTicks: earnedField(r['skippedTicks'], 'skippedTicks', reduced),
    specials: earnedField(r['specials'], 'specials', reduced),
    targetSpecies: strField(r['targetSpecies'], '', MAX_ID_LENGTH),
    // CURSOR_CEILING, which is the MINIMUM of every ceiling any module puts
    // on this cursor — see CURSOR_CEILINGS in time.ts. Naming one of them
    // here is what let SIM-D1 exist: the loader clamped to MAX_SAVE_TICK
    // while every guard tested MAX_TICK, so a write above the smaller number
    // was rewound and re-paid on every reload.
    tick: intField(r['tick'], 0, 0, CURSOR_CEILING),
    variants: variants.tally,
    weightG: earnedField(r['weightG'], 'weightG', reduced),
    xp: earnedField(r['xp'], 'xp', reduced),
  };
  const notes: string[] = [];
  if (dropped > 0) notes.push(`catches-dropped:${dropped}`);
  if (clamped > 0) notes.push(`catches-clamped:${clamped}`);
  // REPORTING IS SYMMETRIC ACROSS BOTH MAPS. never-nerf.test.ts's tell for the
  // round-2 defect was asymmetric reporting — one note on one field and silence
  // on six of the same kind — so the second map gets its own notes on the same
  // two events rather than inheriting the first map's silence.
  if (mastery.dropped > 0) notes.push(`mastery-dropped:${mastery.dropped}`);
  if (mastery.clamped > 0) notes.push(`mastery-clamped:${mastery.clamped}`);
  // AND THE THIRD MAP REPORTS THE SAME TWO EVENTS. never-nerf.test.ts's
  // `reporting is SYMMETRIC` case iterates the earned class rather than naming
  // fields, so silence here would be red rather than quiet.
  if (records.dropped > 0) notes.push(`records-dropped:${records.dropped}`);
  if (records.clamped > 0) notes.push(`records-clamped:${records.clamped}`);
  // AND THE FOURTH. The symmetry case in never-nerf.test.ts iterates the earned
  // class off a real state, so a map that loaded silently would be red there
  // rather than merely undocumented here.
  if (variants.dropped > 0) notes.push(`variants-dropped:${variants.dropped}`);
  if (variants.clamped > 0) notes.push(`variants-clamped:${variants.clamped}`);
  notes.push(...reduced);
  return { sim, notes };
}

function normaliseWardrobe(value: unknown): Wardrobe {
  const r = asRecord(value);
  return {
    equipped: normaliseStringList(r['equipped'], MAX_WARDROBE_KEYS, isCosmeticKey),
    owned: normaliseStringList(r['owned'], MAX_WARDROBE_KEYS, () => true),
  };
}

/**
 * The whole-envelope normaliser, with the notes it accumulated.
 *
 * Idempotent by construction: every field is produced by intField, strField or
 * sanitiseJson, and all three are fixed points on their own output.
 *
 * THE VERSION IS NOT STAMPED DOWN. An earlier version wrote `v: SAVE_VERSION`
 * unconditionally, which did precisely what migrate()'s own comment promises it
 * never does — rewrote a newer save into an older shape and called it upgraded —
 * and did it silently. A future version number is kept, the fields that came
 * with it are carried in `forward`, and the fact is reported in the notes.
 */
function normaliseEnvelopeWithNotes(value: unknown, nowMs = 0): {
  readonly save: SaveEnvelope;
  readonly notes: readonly string[];
} {
  const r = asRecord(value);
  const claimed = intField(r['v'], SAVE_VERSION, 0, MAX_SAVE_VERSION);
  const v = claimed > SAVE_VERSION ? claimed : SAVE_VERSION;
  const sim = normaliseSim(r['sim']);
  const carried = asRecord(r['forward']);
  const notes = [...sim.notes];
  if (v > SAVE_VERSION) notes.push(`future-version:${v}`);
  return {
    save: {
      forward: {
        envelope: budgeted({
          ...asJson(carried['envelope']),
          ...forwardOf(r, KNOWN_ENVELOPE_KEYS),
        }),
        // Unknown keys found now, merged over whatever a previous pass carried,
        // so normalising our own output is a fixed point.
        sim: budgeted({
          ...asJson(carried['sim']),
          ...forwardOf(asRecord(r['sim']), KNOWN_SIM_KEYS),
        }),
      },
      profile: normalisePlayerProfile(r['profile'], nowMs),
      sim: sim.sim,
      v,
      wardrobe: normaliseWardrobe(r['wardrobe']),
    },
    notes,
  };
}

/** A already-sanitised forward bag, re-sanitised. Cheap, and keeps idempotency honest. */
function asJson(value: unknown): { readonly [key: string]: JsonValue } {
  const v = sanitiseJson(value, 1);
  return v !== null && typeof v === 'object' && !Array.isArray(v)
    ? (v as { readonly [key: string]: JsonValue })
    : {};
}

/** The public normaliser. Same function, without the notes. */
export function normaliseEnvelope(value: unknown): SaveEnvelope {
  return normaliseEnvelopeWithNotes(value).save;
}

// ---------------------------------------------------------------------------
// MIGRATION
// ---------------------------------------------------------------------------

/** One step of the chain: reshape a save written at version N into version N+1. */
export type Migration = (raw: Record<string, unknown>, nowMs?: number) => Record<string, unknown>;

/**
 * Keyed by SOURCE version. Must cover 0 .. SAVE_VERSION - 1 with no gaps;
 * save.test.ts asserts it, so bumping SAVE_VERSION without adding a step here
 * fails the suite instead of failing a player.
 */
export const MIGRATIONS: Readonly<Record<number, Migration>> = {
  /**
   * 0 -> 1. Version 0 is anything with no version field: a truncated write, a
   * hand-edited file, a paste from the wrong place. It is lifted by wrapping
   * whatever flat fields it does carry into the v1 envelope, so a partially
   * written save keeps whatever of itself survived rather than being discarded.
   */
  0: (raw) => {
    const hasEnvelope = typeof raw['sim'] === 'object' && raw['sim'] !== null;
    return {
      v: 1,
      sim: hasEnvelope ? raw['sim'] : raw,
      wardrobe: raw['wardrobe'] ?? {},
    };
  },
  1: (raw, nowMs = 0) => {
    const sim = asRecord(raw['sim']);
    const safeState = normaliseSim(sim).sim;
    const accountedPaidTicks = Math.max(0, anchorPaidTicks(safeState) ?? 0);
    return { ...raw, v: 2, profile: { ...createPlayerProfile(nowMs), accountedPaidTicks } };
  },
};

/** Run the chain from whatever version the raw object claims up to SAVE_VERSION. */
function migrate(raw: unknown, nowMs: number): { readonly value: unknown; readonly steps: readonly string[] } {
  let current = asRecord(raw);
  const steps: string[] = [];
  let version = intField(current['v'], 0, 0, MAX_SAVE_VERSION);
  // A save from a FUTURE version is not migrated backwards. It keeps its own
  // version number, every field this build understands is normalised, and every
  // field it does not is carried verbatim in `forward` — never rewriting a newer
  // save into an older shape and calling it upgraded. normaliseEnvelope is what
  // makes that literally true; it used to stamp `v: SAVE_VERSION` on everything
  // and delete the unknown fields, which is the same sentence with the word
  // "never" removed.
  while (version < SAVE_VERSION) {
    const step = MIGRATIONS[version];
    if (step === undefined) break;
    try {
      current = asRecord(step(current, nowMs));
    } catch {
      // A migration that throws on hostile input must not take the run with it.
      steps.push(`v${version}->v${version + 1}:failed`);
      break;
    }
    steps.push(`v${version}->v${version + 1}`);
    version += 1;
  }
  return { value: current, steps };
}

// ---------------------------------------------------------------------------
// EXPORT / IMPORT AS A STRING
// ---------------------------------------------------------------------------

/**
 * THE FORMAT TAG. The family name only - it carries no version digit, because a
 * version digit typed beside SAVE_VERSION is a second copy of SAVE_VERSION.
 */
const SAVE_FORMAT_TAG = 'REELIN';

/** A prefix that announces one exact format version. Built, never typed. */
export function savePrefixFor(version: number): string {
  return `${SAVE_FORMAT_TAG}${intField(version, SAVE_VERSION, 0, MAX_SAVE_VERSION)}`;
}

/**
 * The prefix THIS build writes for its own version.
 *
 * SIM-D3, and the reason this is derived rather than a literal. It read
 * 'REELIN1' while SAVE_VERSION read 1, and nothing related them - so when the
 * save format changed in round 2, neither the prefix nor the storage key moved,
 * and the comment on SAVE_KEY promising that a format change "cannot half-load"
 * described a mechanism that did not operate on the one occasion it existed for.
 * MEASURED then, `node src/sim/measure/run.mjs version-drift`: two hardcoded
 * version literals in src/sim and zero assertions relating either to
 * SAVE_VERSION. The instrument that fails on drift is in save-version.test.ts.
 */
export const SAVE_PREFIX = savePrefixFor(SAVE_VERSION);

/** A prefix, recognised, with the version it announces. Any version - see importSave. */
const SAVE_PREFIX_PATTERN = new RegExp(`^${SAVE_FORMAT_TAG}(\\d{1,7})$`);

/** Enough of a string to know it is meant to be a save at all. */
const SAVE_TEXT_PATTERN = new RegExp(`^${SAVE_FORMAT_TAG}\\d{1,7}\\.`);

/** Deterministic JSON: keys sorted at every depth, so one state has one string. */
export function canonicalJson(value: unknown): string {
  if (value === null || typeof value !== 'object') return JSON.stringify(value) ?? 'null';
  if (Array.isArray(value)) return `[${value.map(canonicalJson).join(',')}]`;
  const rec = value as Record<string, unknown>;
  const parts = Object.keys(rec)
    .sort()
    .map((k) => `${JSON.stringify(k)}:${canonicalJson(rec[k])}`);
  return `{${parts.join(',')}}`;
}

/** FNV-1a 32-bit, as 8 lowercase hex digits. Corruption detection, not security. */
export function checksum(text: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

/**
 * A save as one string the player can copy anywhere.
 * Format: <tag><version>.<checksum8>.<canonical json>
 *
 * THE PREFIX COMES FROM THE ENVELOPE'S OWN VERSION, not from SAVE_VERSION. A
 * save from a newer build keeps its version number through this file (see
 * migrate: a future save is never rewritten into an older shape), so an export
 * of one must keep announcing the version it really is. Deriving the prefix from
 * this build's constant instead would stamp a v2 save as v1 - the same silent
 * downgrade normaliseEnvelope stopped doing to the `v` field, reintroduced one
 * layer out.
 */
export function exportSave(save: SaveEnvelope): string {
  const normalised = normaliseEnvelope(save);
  const body = canonicalJson(normalised);
  return `${savePrefixFor(normalised.v)}.${checksum(body)}.${body}`;
}

export type ImportResult =
  | { readonly ok: true; readonly save: SaveEnvelope; readonly notes: readonly string[] }
  | { readonly ok: false; readonly reason: string };

/** What every door returns: a playable envelope, and what had to be done to get it. */
export interface OpenResult {
  readonly save: SaveEnvelope;
  readonly notes: readonly string[];
}

/** A save envelope around a fresh run. */
export function createSave(seed: number, nowMs: number): SaveEnvelope {
  return {
    forward: { envelope: {}, sim: {} },
    profile: createPlayerProfile(nowMs),
    sim: createState(seed, nowMs),
    v: SAVE_VERSION,
    wardrobe: { equipped: [], owned: [] },
  };
}

// ---------------------------------------------------------------------------
// LIVENESS, AND THE ONE DOOR
// ---------------------------------------------------------------------------

/**
 * CAN THIS STATE EVER ADVANCE AGAIN, STARTING FROM `nowMs`?
 *
 * Three ways the answer is no, and all three are reachable from input a save
 * door accepts:
 *
 *  1. THE CURSOR IS AT THE CEILING. tickAtMs saturates at MAX_TICK. A cursor
 *     sitting there is differenced against a target that can never exceed it, so
 *     `gap` is zero on every advance, forever, with no error raised.
 *  2. THE ANCHOR SATURATES. An epoch far enough in the past makes the TARGET
 *     saturate instead; the first advance then pays the hold and writes the
 *     ceiling into the cursor, arriving at case 1 by itself.
 *  3. THE CURSOR IS AHEAD OF THE CLOCK. A save written under a clock that ran
 *     fast. Nothing is broken, but nothing is earned either until the wall clock
 *     catches up, which for a corrupt value means never in any human sense.
 *
 * This predicate is the whole shape of SIM-C1: a defensive clamp that SATURATES
 * on a value that is later differenced against itself. Asking the question at
 * the door is what stops any of the three from reaching the simulation.
 */
export function isAdvanceable(sim: SimState, nowMs: number): boolean {
  if (!Number.isFinite(nowMs)) return false;
  if (!canAdvanceFrom(sim.epochMs, sim.tick)) return false;
  const target = tickAtMs(sim.epochMs, nowMs);
  // Asked of the TARGET as well as the cursor. advanceWithReport in step.ts asks
  // this exact function the exact same question before it writes, so the door
  // and the guard cannot drift apart - which is what SIM-D1 was.
  if (!canAdvanceFrom(sim.epochMs, target)) return false;
  if (sim.tick > target) return false;
  return true;
}

/**
 * THE REPAIR. Given any state, return one that can advance — and say what was
 * done, so a returning player is never told "repaired" about a save that is
 * still dead.
 *
 * Two moves, in this order, because the first loses nothing at all:
 *
 *  - MOVE THE ANCHOR, KEEP THE CURSOR. Re-place tick 0 so the existing cursor
 *    lands exactly on now. Nothing is earned and nothing is lost; the player
 *    simply resumes. RNG is unaffected because every draw addresses the tick
 *    INDEX, never the wall clock.
 *  - RESET THE CURSOR, KEEP EVERYTHING EARNED. Only when no representable anchor
 *    makes the cursor live. The tick cursor is a clock position, not an earned
 *    quantity: xp, coins, catches, junk, specials, weight and the wardrobe are
 *    all carried over untouched, so this is not a nerf. It is also the only way
 *    out; the alternative is the save the previous version produced, which no
 *    patch could ever have reached.
 *
 * The old function here was reanchorIfAhead, which handled case 3 only and could
 * itself PRODUCE case 1 — it computed an anchor from a saturated cursor, reported
 * "reanchored", and left a save that earned nothing for the rest of time. It is
 * deleted rather than extended: it promised in its own docstring an outcome it
 * could not deliver, and the fix is the guarantee, not the wording.
 */
export function repairCursor(sim: SimState, nowMs: number): RepairResult {
  if (!Number.isFinite(nowMs)) return { sim, notes: [] };
  if (isAdvanceable(sim, nowMs)) return { sim, notes: [] };

  const alignedNow = Math.floor(nowMs / TICK_MS) * TICK_MS;

  const reanchored: SimState = {
    ...sim,
    epochMs: intField(alignedNow - sim.tick * TICK_MS, 0, -MAX_EPOCH_MS, MAX_EPOCH_MS),
  };
  if (isAdvanceable(reanchored, nowMs)) return { sim: reanchored, notes: ['reanchored'] };

  return {
    sim: { ...sim, tick: 0, castProgress: 0, epochMs: alignedNow },
    notes: ['cursor-reset'],
  };
}

export interface RepairResult {
  readonly sim: SimState;
  readonly notes: readonly string[];
}

/**
 * THE ONE DOOR.
 *
 * Every object that becomes a playable save passes through exactly this
 * function: migrate, normalise, repair. There is no second entry point that
 * skips a step, because there is no second entry point — importSave() parses
 * text and then calls this, and instrument.test.ts fails if repairCursor is ever
 * called from anywhere else.
 *
 * The previous version had two siblings whose docstrings claimed the same job:
 * loadSave (documented as the storage path) did NOT repair a future cursor and
 * openSave did. Nothing in the type system, the tests or the call graph forced a
 * caller onto the repaired one, and neither had a production caller at all, so
 * the divergence was free to exist. It cannot recur: there is one function.
 *
 * Never throws, for any input at all.
 */
function openEnvelope(raw: unknown, nowMs: number): OpenResult {
  const migrated = migrate(raw, nowMs);
  const normalised = normaliseEnvelopeWithNotes(migrated.value, nowMs);
  const repaired = repairCursor(normalised.save.sim, nowMs);
  return {
    save: { ...normalised.save, sim: repaired.sim },
    notes: [...migrated.steps, ...normalised.notes, ...repaired.notes],
  };
}

export function openSave(raw: unknown, seed: number, nowMs: number): OpenResult {
  if (raw === null || raw === undefined) {
    return { save: createSave(seed, nowMs), notes: ['fresh'] };
  }
  // A caller handing this door an EXPORT STRING is handing it a save, and the
  // silent failure — treating the string as an object with no fields and
  // returning a fresh run — is data loss with no message. So the door
  // recognises its own format.
  //
  // It delegates to importSave, which parses and then calls openEnvelope
  // DIRECTLY rather than coming back through here. That is deliberate: a body
  // that parses to another export string would otherwise re-enter this branch,
  // and a save nested that way could be built to recurse until the stack gives
  // out. There is exactly one hop, and it is not a cycle.
  if (typeof raw === 'string' && SAVE_TEXT_PATTERN.test(raw.trim())) {
    const parsed = importSave(raw, nowMs);
    return parsed.ok
      ? { save: parsed.save, notes: parsed.notes }
      : { save: createSave(seed, nowMs), notes: [`fresh:${parsed.reason}`] };
  }
  return openEnvelope(raw, nowMs);
}

/**
 * Parse an exported string and open it. NEVER THROWS, for any input at all —
 * every failure comes back as ok:false with a reason a player could act on, so a
 * bad paste is a message and not a lost run.
 *
 * It takes no seed, because it never invents a run: a string that is not a save
 * is a refusal, and deciding what to do about that belongs to the caller who
 * knows whether there is a run already in progress. openSession does.
 */
export function importSave(text: unknown, nowMs: number): ImportResult {
  if (typeof text !== 'string') return { ok: false, reason: 'not-a-string' };
  const trimmed = text.trim();
  if (trimmed === '') return { ok: false, reason: 'empty' };

  const firstDot = trimmed.indexOf('.');
  const secondDot = firstDot < 0 ? -1 : trimmed.indexOf('.', firstDot + 1);
  if (firstDot < 0 || secondDot < 0) return { ok: false, reason: 'malformed' };

  const prefix = trimmed.slice(0, firstDot);
  // ANY version of this format is recognised, and the one it announces is
  // reported. Refusing a newer prefix outright would throw away a save the
  // envelope machinery can carry perfectly well - migrate() keeps a future
  // version number and forwardOf() keeps the fields that came with it - and
  // accepting it while pretending it is ours is the half-load SIM-D3 named.
  const announced = SAVE_PREFIX_PATTERN.exec(prefix);
  if (announced === null) return { ok: false, reason: 'wrong-prefix' };
  const prefixVersion = intField(announced[1], SAVE_VERSION, 0, MAX_SAVE_VERSION);

  const stamp = trimmed.slice(firstDot + 1, secondDot);
  const body = trimmed.slice(secondDot + 1);

  const notes: string[] = [];
  if (checksum(body) !== stamp) {
    // A bad checksum is a warning, not a refusal. The alternative is telling a
    // player their run is gone because one character changed in transit, when
    // the normaliser can very often recover the rest of it intact.
    notes.push('checksum-mismatch');
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(body, (key, value: unknown) =>
      POISON_KEYS.has(key) ? undefined : value,
    );
  } catch {
    return { ok: false, reason: 'unparseable' };
  }

  const opened = openEnvelope(parsed, nowMs);
  // The tag and the body disagreeing about the version is worth saying out loud:
  // one of them was written by hand, and which one is not knowable from here.
  if (prefixVersion !== opened.save.v) notes.push(`prefix-version:${prefixVersion}`);
  return { ok: true, save: opened.save, notes: [...notes, ...opened.notes] };
}
